import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Loader2 } from "lucide-react";
import { Page, PageHeader } from "../../components/PageHeader";
import { api, extractError } from "../../lib/api";
import { fmtCLP } from "../../lib/format";
import { usePermissions } from "../../hooks/usePermissions";
import { useReadOnly } from "../../hooks/useReadOnly";
import EditorInforme from "./EditorInforme";
import Cartera from "./Cartera";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const fmtDate = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}` : s || "");

function Alertas({ r }) {
  if (!r) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-2 text-[12px]">
      {r.bloqueos?.length > 0 && (
        <span
          className="rounded-sm bg-destructive/10 px-1.5 py-0.5 text-[10.5px] font-semibold text-destructive"
          title={r.bloqueos.map((b) => `${b.id} ${b.control}`).join(" · ")}
        >
          {r.bloqueos.length} bloqueo{r.bloqueos.length > 1 ? "s" : ""}
        </span>
      )}
      {r.rojos > 0 && (
        <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400">
          <span className="h-2 w-2 rounded-full bg-current" /> {r.rojos}
        </span>
      )}
      {r.amarillos > 0 && (
        <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
          <span className="h-2 w-2 rounded-full bg-current" /> {r.amarillos}
        </span>
      )}
      {!r.rojos && !r.amarillos && <span className="text-emerald-700 dark:text-emerald-400">En orden</span>}
    </span>
  );
}

export default function InformeTributario() {
  const { empresaActiva } = useOutletContext() || {};
  const { canWrite } = usePermissions();
  const readOnly = useReadOnly() || !canWrite("informe_tributario");
  const [searchParams, setSearchParams] = useSearchParams();
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editor, setEditor] = useState(() => {
    const id = searchParams.get("informe");
    if (id) return { id };
    return searchParams.get("nuevo") ? { id: null } : null;
  });
  const [pestana, setPestana] = useState(() => (searchParams.get("vista") === "cartera" ? "cartera" : "empresa"));
  const [aEliminar, setAEliminar] = useState(null);

  useEffect(() => {
    if (searchParams.has("informe") || searchParams.has("nuevo") || searchParams.has("vista")) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirDesdeCartera = async (fila) => {
    const destino = `${fila.ultimo ? `informe=${encodeURIComponent(fila.ultimo.id)}` : "nuevo=1"}&vista=cartera`;
    if (fila.empresa_id === empresaActiva?.id) {
      setEditor(fila.ultimo ? { id: fila.ultimo.id } : { id: null });
      return;
    }
    try {
      await api.post("/empresas/activa", { empresa_id: fila.empresa_id });
      window.location.assign(`${window.location.pathname}?${destino}`);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get("/informes-tributarios");
      setLista(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  const empresaAnterior = useRef(empresaActiva?.id);
  useEffect(() => {
    if (empresaAnterior.current && empresaAnterior.current !== empresaActiva?.id) setEditor(null);
    empresaAnterior.current = empresaActiva?.id;
    cargar();
  }, [empresaActiva?.id, cargar]);

  const eliminar = async () => {
    if (!aEliminar) return;
    try {
      await api.delete(`/informes-tributarios/${aEliminar.id}`);
      toast.success("Informe eliminado");
      setAEliminar(null);
      cargar();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  if (editor) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
        <EditorInforme
          key={editor.id || "nuevo"}
          informeId={editor.id}
          empresaActiva={empresaActiva}
          readOnly={readOnly}
          onVolver={() => {
            setEditor(null);
            cargar();
          }}
          onGuardado={cargar}
        />
      </div>
    );
  }

  return (
    <Page>
      <PageHeader
        title="Informe tributario integral"
        subtitle={
          pestana === "cartera"
            ? "Semáforo tributario de todos tus clientes según su último informe: quién tiene deuda, alertas o un informe por actualizar."
            : `Estado tributario a la fecha de ${empresaActiva?.nombre || "la empresa activa"}: carpeta tributaria del SII, deuda TGR y registros de Aequor, para anticipar la renta.`
        }
        testid="informe-tributario-header"
        actions={
          pestana === "empresa" && (
            <button
              type="button"
              onClick={() => setEditor({ id: null })}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm"
              data-testid="informe-nuevo"
            >
              <Plus className="h-4 w-4" /> Nuevo informe
            </button>
          )
        }
      />

      <div className="flex border-b border-border mb-5 gap-1" role="tablist">
        {[
          ["empresa", empresaActiva?.nombre ? `Historial · ${empresaActiva.nombre}` : "Historial de la empresa"],
          ["cartera", "Cartera de clientes"],
        ].map(([v, l]) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={pestana === v}
            onClick={() => setPestana(v)}
            className={`px-3 py-2 text-[13px] -mb-px border-b-2 transition-colors max-w-[60vw] truncate ${
              pestana === v ? "border-primary text-foreground font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`informe-pestana-${v}`}
          >
            {l}
          </button>
        ))}
      </div>

      {pestana === "cartera" ? (
        <Cartera empresaActivaId={empresaActiva?.id} onAbrir={abrirDesdeCartera} />
      ) : cargando ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando historial…
        </div>
      ) : lista.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm bg-card px-6 py-12 text-center" data-testid="informe-vacio">
          <FileText className="h-8 w-8 mx-auto text-gold mb-3" strokeWidth={1.5} />
          <h2 className="font-display text-xl text-foreground">Aún no hay informes para esta empresa</h2>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto mt-2">
            Descarga desde sii.cl la carpeta tributaria del cliente y, desde tgr.cl, su certificado de deuda. Al subirlos, Aequor arma el informe
            para el cliente y tu hoja de trabajo, y los cruza con los honorarios, remuneraciones, libro mayor y registro de compras y ventas de la empresa.
          </p>
          <button
            type="button"
            onClick={() => setEditor({ id: null })}
            className="mt-5 inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm"
          >
            <Plus className="h-4 w-4" /> Crear el primero
          </button>
        </div>
      ) : (
        <div className="border border-border rounded-sm bg-card overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="informe-historial">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="label-caps px-4 py-3">Corte</th>
                <th className="label-caps px-4 py-3">Período</th>
                <th className="label-caps px-4 py-3 text-right">Ventas</th>
                <th className="label-caps px-4 py-3 text-right">Resultado a la fecha</th>
                <th className="label-caps px-4 py-3 text-right">Renta proyectada</th>
                <th className="label-caps px-4 py-3 text-right">Deuda TGR</th>
                <th className="label-caps px-4 py-3">Alertas</th>
                <th className="label-caps px-4 py-3">Estado</th>
                <th className="label-caps px-4 py-3">Actualizado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {lista.map((inf) => {
                const r = inf.resumen || {};
                return (
                  <tr
                    key={inf.id}
                    className="border-b border-border/60 last:border-0 hover:bg-muted/40 cursor-pointer"
                    onClick={() => setEditor({ id: inf.id })}
                    data-testid={`informe-fila-${inf.id}`}
                  >
                    <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">{fmtDate(inf.fecha_corte)}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {r.ultimoMes ? `ene – ${MESES[r.ultimoMes - 1]} ${inf.anio}` : inf.anio}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.ventas != null ? fmtCLP(r.ventas) : "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.resultado != null ? fmtCLP(r.resultado) : "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {r.renta == null ? "—" : r.renta > 0 ? fmtCLP(r.renta) : <span className="text-emerald-700 dark:text-emerald-400">Devolución {fmtCLP(-r.renta)}</span>}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums ${r.morosa > 0 ? "text-red-700 dark:text-red-400 font-medium" : ""}`}>
                      {r.deudaTGR == null ? "—" : fmtCLP(r.deudaTGR)}
                    </td>
                    <td className="px-4 py-3"><Alertas r={inf.resumen} /></td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-[10px] tracking-[0.15em] uppercase font-semibold px-2 py-0.5 rounded-sm ${
                          inf.estado === "emitido" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {inf.estado === "emitido" ? "Emitido" : "Borrador"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-muted-foreground whitespace-nowrap">
                      {fmtDate((inf.updated_at || inf.created_at || "").slice(0, 10))}
                      <div className="truncate max-w-[140px]">{inf.updated_by_nombre || inf.created_by_nombre || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAEliminar(inf);
                          }}
                          className="p-1.5 rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          title="Eliminar informe"
                          data-testid={`informe-eliminar-${inf.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!aEliminar} onOpenChange={(o) => !o && setAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este informe?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borrará el informe con corte {aEliminar ? fmtDate(aEliminar.fecha_corte) : ""} y sus supuestos. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
